import React, { useState } from 'react';

/**
 * ExperimentLog - Save, view, and compare simulation experiments.
 * Captures: dose, scenario modifiers, antidote usage, and outcome data.
 */
const ExperimentLog = ({ experiments, onSave, onDelete, onClear, currentCanSave }) => {
  const [expanded, setExpanded] = useState(true);
  const [compareIds, setCompareIds] = useState([]);

  const toggleCompare = (id) => {
    setCompareIds(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  };

  const comparing = experiments.filter(e => compareIds.includes(e.id));

  const exportReport = () => {
    const pad = (str, len) => String(str).padEnd(len);
    const padR = (str, len) => String(str).padStart(len);

    let report = '';
    report += '═══════════════════════════════════════════════════════════════\n';
    report += '  ACETAMINOPHEN METABOLISM — EXPERIMENT LOG\n';
    report += '  Generated: ' + new Date().toLocaleString() + '\n';
    report += '═══════════════════════════════════════════════════════════════\n\n';

    // Individual experiment details
    experiments.forEach((exp) => {
      report += '───────────────────────────────────────────────────────────────\n';
      report += `  EXPERIMENT #${exp.id}    ${exp.timestamp}\n`;
      report += '───────────────────────────────────────────────────────────────\n';
      report += `  Dose:        ${exp.doseLabel} (${exp.doseAmount} units)\n`;
      report += `  Scenarios:   ${exp.modifiers.length > 0 ? exp.modifiers.join(', ') : 'None'}\n`;
      report += `  Antidote:    ${exp.nacUsed ? 'NAC administered at cycle ' + exp.nacTiming : 'None'}\n`;
      report += `  Outcome:     ${exp.outcome === 'win' ? 'HEPATOCYTE SURVIVED' : 'HEPATOCYTE LOST'}\n\n`;
      report += `  Results:\n`;
      report += `    Cycles survived:        ${exp.ticksSurvived}\n`;
      report += `    Final cell health:      ${exp.finalHealth.toFixed(1)}%\n`;
      report += `    Mitochondrial integrity: ${exp.finalMitoIntegrity.toFixed(1)}%\n`;
      report += `    GSH remaining:          ${exp.gshRemaining.toFixed(0)}%\n\n`;
      report += `  Drug Fate (% of dose):\n`;
      report += `    Glucuronidation (UGT):  ${exp.glucuronidePct.toFixed(1)}%\n`;
      report += `    Sulfation (SULT):       ${exp.sulfatePct.toFixed(1)}%\n`;
      report += `    GSH conjugation:        ${exp.gshConjugatePct.toFixed(1)}%\n`;
      report += `    APAP remaining:         ${exp.apapRemaining.toFixed(1)}%\n`;
      report += `    NAPQI (unconjugated):   ${exp.napqiAmount.toFixed(1)}\n`;
      if (exp.factors && exp.factors.length > 0) {
        report += `\n  Key Factors:\n`;
        exp.factors.forEach(f => { report += `    • ${f}\n`; });
      }
      report += '\n';
    });

    // Comparison table if multiple experiments
    if (experiments.length > 1) {
      report += '\n═══════════════════════════════════════════════════════════════\n';
      report += '  COMPARISON TABLE\n';
      report += '═══════════════════════════════════════════════════════════════\n\n';

      const col = 14;
      const labelCol = 20;
      const exps = experiments;

      const header = pad('', labelCol) + exps.map(e => padR('#' + e.id, col)).join('');
      report += header + '\n';
      report += '─'.repeat(labelCol + exps.length * col) + '\n';

      const rows = [
        ['Dose', e => e.doseAmount + ' units'],
        ['Outcome', e => e.outcome === 'win' ? 'Survived' : 'Lost'],
        ['Scenarios', e => e.modifiers.length > 0 ? e.modifiers.join(', ') : 'None'],
        ['Antidote', e => e.nacUsed ? 'NAC @ ' + e.nacTiming : 'No'],
        ['Cycles', e => String(e.ticksSurvived)],
        ['Health', e => e.finalHealth.toFixed(1) + '%'],
        ['GSH remaining', e => e.gshRemaining.toFixed(0) + '%'],
        ['UGT', e => e.glucuronidePct.toFixed(1) + '%'],
        ['SULT', e => e.sulfatePct.toFixed(1) + '%'],
        ['GSH conjugation', e => e.gshConjugatePct.toFixed(1) + '%'],
        ['APAP remaining', e => e.apapRemaining.toFixed(1) + '%'],
        ['NAPQI', e => e.napqiAmount.toFixed(1)],
      ];

      rows.forEach(([label, fn]) => {
        report += pad(label, labelCol) + exps.map(e => padR(fn(e), col)).join('') + '\n';
      });
    }

    report += '\n═══════════════════════════════════════════════════════════════\n';
    report += '  End of Report\n';
    report += '═══════════════════════════════════════════════════════════════\n';

    const blob = new Blob([report], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'APAP_Experiment_Log_' + new Date().toISOString().slice(0, 10) + '.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  const exportChart = () => {
    const exps = experiments;
    const labels = exps.map(e => {
      let label = '#' + e.id + ' ' + e.doseAmount + 'u';
      if (e.modifiers.length > 0) label += ' +' + e.modifiers[0].split(' ')[0];
      if (e.nacUsed) label += ' +NAC';
      return label;
    });
    const dataJSON = JSON.stringify({
      labels,
      ugt: exps.map(e => +e.glucuronidePct.toFixed(1)),
      sult: exps.map(e => +e.sulfatePct.toFixed(1)),
      gshConj: exps.map(e => +e.gshConjugatePct.toFixed(1)),
      apapRem: exps.map(e => +e.apapRemaining.toFixed(1)),
      napqi: exps.map(e => +e.napqiAmount.toFixed(1)),
      health: exps.map(e => +e.finalHealth.toFixed(1)),
      outcomes: exps.map(e => e.outcome),
      gsh: exps.map(e => +e.gshRemaining.toFixed(1)),
      rows: exps.map(e => ({
        id: e.id,
        dose: e.doseAmount,
        doseLabel: e.doseLabel,
        outcome: e.outcome === 'win' ? 'Survived' : 'Lost',
        scenarios: e.modifiers.length > 0 ? e.modifiers.join(', ') : 'None',
        antidote: e.nacUsed ? 'NAC @ cycle ' + e.nacTiming : 'None',
        cycles: e.ticksSurvived,
        health: e.finalHealth.toFixed(1),
        mito: e.finalMitoIntegrity.toFixed(1),
        gsh: e.gshRemaining.toFixed(1),
        ugt: e.glucuronidePct.toFixed(1),
        sult: e.sulfatePct.toFixed(1),
        gshConj: e.gshConjugatePct.toFixed(1),
        apapRem: e.apapRemaining.toFixed(1),
        napqi: e.napqiAmount.toFixed(1),
      })),
    });

    const html = `<!DOCTYPE html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>APAP Metabolism Lab — Experiment Results</title>
<script src="https://cdnjs.cloudflare.com/ajax/libs/Chart.js/4.4.1/chart.umd.min.js"><\/script>
<style>
*{box-sizing:border-box;margin:0;padding:0}
body{background:#1a1a2e;color:#e0e0e0;font-family:'Segoe UI',system-ui,-apple-system,sans-serif;padding:24px}
.header{text-align:center;margin-bottom:32px;border-bottom:1px solid #2a3a5c;padding-bottom:16px}
.header h1{font-size:1.6rem;color:#8ab4f8;margin-bottom:4px}
.header p{font-size:0.8rem;color:#888}
.toggle-row{display:flex;justify-content:center;gap:8px;margin-bottom:24px}
.toggle-btn{padding:8px 24px;border:1px solid #2a3a5c;border-radius:6px;background:#16213e;color:#888;font-size:0.85rem;cursor:pointer;transition:all .2s}
.toggle-btn.active{border-color:#8ab4f8;color:#8ab4f8;background:rgba(138,180,248,0.1)}
.view{display:none}
.view.active{display:block}
.chart-grid{display:grid;grid-template-columns:1fr;gap:24px;max-width:900px;margin:0 auto}
.chart-card{background:rgba(255,255,255,0.03);border:1px solid #2a3a5c;border-radius:12px;padding:20px}
.chart-card h3{font-size:0.9rem;color:#8ab4f8;margin-bottom:12px}
canvas{max-height:320px}
table{width:100%;border-collapse:collapse;font-size:0.8rem;max-width:1100px;margin:0 auto}
th,td{padding:8px 10px;text-align:left;border-bottom:1px solid rgba(255,255,255,0.06)}
th{color:#8ab4f8;font-weight:600;font-size:0.75rem;text-transform:uppercase;letter-spacing:0.5px}
td{color:#ccc}
tr:hover td{background:rgba(255,255,255,0.03)}
.bar-cell{position:relative;min-width:80px}
.bar-inner{height:6px;border-radius:3px;margin-top:4px}
.outcome-win{color:#4CAF50;font-weight:600}
.outcome-lose{color:#f44336;font-weight:600}
.footnote{text-align:center;margin-top:32px;padding-top:16px;border-top:1px solid #2a3a5c;font-size:0.7rem;color:#666}
@media print{body{background:#fff;color:#222}th{color:#333}.chart-card{border-color:#ccc}.footnote{color:#999}}
</style>
</head>
<body>
<div class="header">
  <h1>APAP Metabolism Lab &mdash; Experiment Results</h1>
  <p>Generated: ${new Date().toLocaleString()}</p>
</div>
<div class="toggle-row">
  <button class="toggle-btn active" onclick="showView('charts')">Charts</button>
  <button class="toggle-btn" onclick="showView('table')">Data Table</button>
</div>

<div id="view-charts" class="view active">
  <div class="chart-grid">
    <div class="chart-card"><h3>Drug Fate (% of Dose)</h3><canvas id="fateChart"></canvas></div>
    <div class="chart-card"><h3>Final Cell Health</h3><canvas id="healthChart"></canvas></div>
    <div class="chart-card"><h3>GSH Remaining</h3><canvas id="gshChart"></canvas></div>
  </div>
</div>

<div id="view-table" class="view">
  <table id="dataTable"></table>
</div>

<div class="footnote">GSH critical threshold &lt; 30% &mdash; Mitchell et al., 1973</div>

<script>
const D = ${dataJSON};

function showView(v) {
  document.querySelectorAll('.view').forEach(el => el.classList.remove('active'));
  document.querySelectorAll('.toggle-btn').forEach(el => el.classList.remove('active'));
  document.getElementById('view-' + v).classList.add('active');
  event.target.classList.add('active');
}

const chartDefaults = {
  color: '#ccc',
  borderColor: 'rgba(255,255,255,0.1)',
};
Chart.defaults.color = '#ccc';
Chart.defaults.borderColor = 'rgba(255,255,255,0.1)';

// Chart 1: Stacked bar — Drug Fate
new Chart(document.getElementById('fateChart'), {
  type: 'bar',
  data: {
    labels: D.labels,
    datasets: [
      { label: 'UGT (Glucuronidation)', data: D.ugt, backgroundColor: '#1B5E20' },
      { label: 'SULT (Sulfation)', data: D.sult, backgroundColor: '#0D47A1' },
      { label: 'GSH Conjugation', data: D.gshConj, backgroundColor: '#8B6914' },
      { label: 'APAP Remaining', data: D.apapRem, backgroundColor: '#616161' },
      { label: 'NAPQI (unconjugated)', data: D.napqi, backgroundColor: '#8B0000' },
    ],
  },
  options: {
    responsive: true,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, padding: 12, font: { size: 11 } } },
      tooltip: { callbacks: { label: ctx => ctx.dataset.label + ': ' + ctx.raw + '%' } },
    },
    scales: {
      x: { stacked: true, ticks: { font: { size: 11 } } },
      y: { stacked: true, max: 105, title: { display: true, text: '% of Dose', font: { size: 11 } } },
    },
  },
});

// Chart 2: Cell Health
new Chart(document.getElementById('healthChart'), {
  type: 'bar',
  data: {
    labels: D.labels,
    datasets: [{
      label: 'Cell Health',
      data: D.health,
      backgroundColor: D.outcomes.map(o => o === 'win' ? '#2E7D32' : '#C62828'),
      borderRadius: 4,
    }],
  },
  options: {
    responsive: true,
    plugins: { legend: { display: false } },
    scales: {
      y: { min: 0, max: 105, title: { display: true, text: 'Health %', font: { size: 11 } } },
    },
  },
});

// Chart 3: GSH Remaining
new Chart(document.getElementById('gshChart'), {
  type: 'bar',
  data: {
    labels: D.labels,
    datasets: [{
      label: 'GSH Remaining',
      data: D.gsh,
      backgroundColor: D.gsh.map(v => v >= 30 ? '#1565C0' : v >= 10 ? '#F57F17' : '#C62828'),
      borderRadius: 4,
    }],
  },
  options: {
    responsive: true,
    plugins: {
      legend: { display: false },
      annotation: {},
    },
    scales: {
      y: { min: 0, max: 105, title: { display: true, text: 'GSH %', font: { size: 11 } } },
    },
  },
  plugins: [{
    id: 'thresholdLine',
    afterDraw(chart) {
      const yScale = chart.scales.y;
      const y = yScale.getPixelForValue(30);
      const ctx = chart.ctx;
      ctx.save();
      ctx.strokeStyle = '#F57F17';
      ctx.lineWidth = 1.5;
      ctx.setLineDash([6, 4]);
      ctx.beginPath();
      ctx.moveTo(chart.chartArea.left, y);
      ctx.lineTo(chart.chartArea.right, y);
      ctx.stroke();
      ctx.fillStyle = '#F57F17';
      ctx.font = '10px sans-serif';
      ctx.fillText('30% Mitchell threshold', chart.chartArea.left + 4, y - 5);
      ctx.restore();
    }
  }],
});

// Data Table
(function buildTable() {
  const t = document.getElementById('dataTable');
  const cols = [
    { key: 'id', label: '#' },
    { key: 'doseLabel', label: 'Dose' },
    { key: 'outcome', label: 'Outcome' },
    { key: 'scenarios', label: 'Scenarios' },
    { key: 'antidote', label: 'Antidote' },
    { key: 'cycles', label: 'Cycles' },
    { key: 'health', label: 'Health %', bar: true, color: r => r.outcome === 'Survived' ? '#2E7D32' : '#C62828' },
    { key: 'mito', label: 'Mito %', bar: true, color: () => '#1565C0' },
    { key: 'gsh', label: 'GSH %', bar: true, color: r => parseFloat(r.gsh) >= 30 ? '#1565C0' : parseFloat(r.gsh) >= 10 ? '#F57F17' : '#C62828' },
    { key: 'ugt', label: 'UGT %', bar: true, color: () => '#1B5E20' },
    { key: 'sult', label: 'SULT %', bar: true, color: () => '#0D47A1' },
    { key: 'gshConj', label: 'GSH Conj %', bar: true, color: () => '#8B6914' },
    { key: 'apapRem', label: 'APAP Rem %', bar: true, color: () => '#616161' },
    { key: 'napqi', label: 'NAPQI', bar: false },
  ];
  let html = '<thead><tr>' + cols.map(c => '<th>' + c.label + '</th>').join('') + '</tr></thead><tbody>';
  D.rows.forEach(r => {
    html += '<tr>';
    cols.forEach(c => {
      const v = r[c.key];
      const cls = c.key === 'outcome' ? (v === 'Survived' ? 'outcome-win' : 'outcome-lose') : '';
      if (c.bar) {
        const pct = Math.min(100, Math.max(0, parseFloat(v)));
        html += '<td class="bar-cell ' + cls + '">' + v + '%<div class="bar-inner" style="width:' + pct + '%;background:' + c.color(r) + '"></div></td>';
      } else {
        html += '<td class="' + cls + '">' + v + '</td>';
      }
    });
    html += '</tr>';
  });
  html += '</tbody>';
  t.innerHTML = html;
})();
<\/script>
</body>
</html>`;

    const fileName = 'APAP_Chart_' + new Date().toISOString().slice(0, 10) + '.html';
    // Try Capacitor (iOS) first, fall back to browser Blob download
    if (window.Capacitor && window.Capacitor.Plugins && window.Capacitor.Plugins.Filesystem) {
      const { Filesystem, Share } = window.Capacitor.Plugins;
      Filesystem.writeFile({
        path: fileName,
        data: btoa(unescape(encodeURIComponent(html))),
        directory: 'CACHE',
        encoding: 'utf8',
      }).then(result => {
        Share.share({
          title: 'APAP Experiment Chart',
          url: result.uri,
        });
      }).catch(() => {
        // Fallback if Capacitor write fails
        downloadBlob(html, fileName);
      });
    } else {
      downloadBlob(html, fileName);
    }
  };

  const downloadBlob = (content, fileName) => {
    const blob = new Blob([content], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="experiment-log">
      <div className="experiment-header" onClick={() => setExpanded(!expanded)}>
        <h3>Experiment Log ({experiments.length})</h3>
        <span className="expand-toggle">{expanded ? '▾' : '▸'}</span>
      </div>

      {expanded && (
        <div className="experiment-body">
          {currentCanSave && (
            <button className="btn btn-save-experiment" onClick={onSave}>
              Save Current Experiment
            </button>
          )}

          {experiments.length === 0 && (
            <p className="experiment-empty">
              Run a simulation to completion, then save it here to compare results.
            </p>
          )}

          {experiments.length > 0 && (
            <>
              <div className="experiment-list">
                {experiments.map((exp) => (
                  <div key={exp.id} className={`experiment-card ${exp.outcome === 'win' ? 'card-win' : 'card-lose'}`}>
                    <div className="experiment-card-header">
                      <strong>#{exp.id}</strong>
                      <span className={`outcome-badge ${exp.outcome}`}>
                        {exp.outcome === 'win' ? 'Survived' : 'Lost'}
                      </span>
                      <button
                        className={`btn-compare ${compareIds.includes(exp.id) ? 'active' : ''}`}
                        onClick={() => toggleCompare(exp.id)}
                        title="Toggle compare"
                      >
                        {compareIds.includes(exp.id) ? '✓' : '⇔'}
                      </button>
                      <button
                        className="btn-delete-exp"
                        onClick={() => onDelete(exp.id)}
                        title="Delete"
                      >
                        ×
                      </button>
                    </div>

                    <div className="experiment-config">
                      <div className="config-row">
                        <span className="config-label">Dose:</span>
                        <span>{exp.doseLabel} ({exp.doseAmount} units)</span>
                      </div>
                      {exp.modifiers.length > 0 && (
                        <div className="config-row">
                          <span className="config-label">Scenario:</span>
                          <span>{exp.modifiers.join(', ')}</span>
                        </div>
                      )}
                      <div className="config-row">
                        <span className="config-label">Antidote:</span>
                        <span>{exp.nacUsed ? `NAC (cycle ${exp.nacTiming})` : 'None'}</span>
                      </div>
                    </div>

                    <div className="experiment-results">
                      <div className="result-row">
                        <span>Cycles: {exp.ticksSurvived}</span>
                        <span>Health: {exp.finalHealth.toFixed(1)}%</span>
                      </div>
                      <div className="result-row">
                        <span>GSH: {exp.gshRemaining.toFixed(0)}%</span>
                        <span>Mito: {exp.finalMitoIntegrity.toFixed(1)}%</span>
                      </div>
                      <div className="result-fate">
                        <span className="fate-safe">UGT {exp.glucuronidePct.toFixed(0)}%</span>
                        <span className="fate-safe">SULT {exp.sulfatePct.toFixed(0)}%</span>
                        <span className="fate-safe">GSH {exp.gshConjugatePct.toFixed(0)}%</span>
                        <span className="fate-toxic">NAPQI {exp.napqiAmount.toFixed(1)}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {comparing.length >= 2 && (
                <div className="experiment-compare">
                  <h4>Comparison</h4>
                  <table className="compare-table">
                    <thead>
                      <tr>
                        <th></th>
                        {comparing.map(e => (
                          <th key={e.id}>#{e.id}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Dose</td>
                        {comparing.map(e => <td key={e.id}>{e.doseAmount}</td>)}
                      </tr>
                      <tr>
                        <td>Outcome</td>
                        {comparing.map(e => (
                          <td key={e.id} className={e.outcome === 'win' ? 'cell-win' : 'cell-lose'}>
                            {e.outcome === 'win' ? 'Survived' : 'Lost'}
                          </td>
                        ))}
                      </tr>
                      <tr>
                        <td>Scenarios</td>
                        {comparing.map(e => (
                          <td key={e.id}>{e.modifiers.length > 0 ? e.modifiers.join(', ') : 'None'}</td>
                        ))}
                      </tr>
                      <tr>
                        <td>Antidote</td>
                        {comparing.map(e => (
                          <td key={e.id}>{e.nacUsed ? `NAC @ ${e.nacTiming}` : 'No'}</td>
                        ))}
                      </tr>
                      <tr>
                        <td>Cycles</td>
                        {comparing.map(e => <td key={e.id}>{e.ticksSurvived}</td>)}
                      </tr>
                      <tr>
                        <td>Health</td>
                        {comparing.map(e => <td key={e.id}>{e.finalHealth.toFixed(1)}%</td>)}
                      </tr>
                      <tr>
                        <td>GSH</td>
                        {comparing.map(e => <td key={e.id}>{e.gshRemaining.toFixed(0)}%</td>)}
                      </tr>
                      <tr>
                        <td>UGT</td>
                        {comparing.map(e => <td key={e.id}>{e.glucuronidePct.toFixed(0)}%</td>)}
                      </tr>
                      <tr>
                        <td>SULT</td>
                        {comparing.map(e => <td key={e.id}>{e.sulfatePct.toFixed(0)}%</td>)}
                      </tr>
                      <tr>
                        <td>GSH conj.</td>
                        {comparing.map(e => <td key={e.id}>{e.gshConjugatePct.toFixed(0)}%</td>)}
                      </tr>
                      <tr>
                        <td>NAPQI</td>
                        {comparing.map(e => (
                          <td key={e.id} className={e.napqiAmount > 5 ? 'cell-lose' : ''}>
                            {e.napqiAmount.toFixed(1)}
                          </td>
                        ))}
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}

              <div className="experiment-actions">
                <div className="experiment-export-row">
                  <button className="btn btn-export-experiments" onClick={exportReport}>
                    Export as text
                  </button>
                  <button className="btn btn-export-chart" onClick={exportChart}>
                    Export as chart
                  </button>
                </div>
                {experiments.length > 1 && (
                  <button className="btn btn-clear-experiments" onClick={onClear}>
                    Clear All Experiments
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default ExperimentLog;
